import React, { useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertCircle, Building2, User, Mail, Phone, MapPin, FileText, Key, Copy } from 'lucide-react'
import { ApiService } from '@/services/api'
import type { Restaurant } from '@/types'
import { COUNTRIES, DEFAULT_COUNTRY, getCurrencyForCountry } from '@/lib/countries'
import { getCurrencySymbol } from '@/lib/utils'

interface CreateRestaurantFormProps {
  open: boolean
  onClose: () => void
  onSuccess: (restaurant: Restaurant) => void
}

interface FormData {
  name: string
  ownerName: string
  email: string
  phone: string
  password: string
  country: string
  address: {
    line1: string
    line2: string
    city: string
    state: string
    pincode: string
  }
  gstin: string
  planType: 'yearly'
  subscriptionAmount: number
  adminNotes: string
}

const initialFormData: FormData = {
  name: '',
  ownerName: '',
  email: '',
  phone: '',
  password: '',
  country: DEFAULT_COUNTRY,
  address: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    pincode: ''
  },
  gstin: '',
  planType: 'yearly',
  subscriptionAmount: 12000,
  adminNotes: ''
}

export default function CreateRestaurantForm({ open, onClose, onSuccess }: CreateRestaurantFormProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [generatedPassword, setGeneratedPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const isIndia = formData.country === DEFAULT_COUNTRY
  const currencyCode = getCurrencyForCountry(formData.country)
  const currencySymbol = getCurrencySymbol(currencyCode)

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1]
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const generateDatabaseName = (restaurantName: string) => {
    return `${restaurantName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')}`
  }

  const generatePassword = () => {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*'
    let password = ''
    for (let i = 0; i < 12; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length))
    }
    return password
  }

  const handleGeneratePassword = () => {
    const newPassword = generatePassword()
    setGeneratedPassword(newPassword)
    setFormData(prev => ({ ...prev, password: newPassword }))
    setShowPassword(true)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      // Validate that password is generated
      if (!formData.password) {
        setError('Please generate a password for the restaurant owner')
        setIsLoading(false)
        return
      }

      // Create restaurant data with subscription details
      const restaurantData = {
        ...formData,
        databaseName: generateDatabaseName(formData.name),
        subscriptionStatus: 'active' as const,
        subscriptionStartDate: new Date(),
        subscriptionEndDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year from now
        paymentStatus: 'paid' as const,
        subscriptionAmount: formData.subscriptionAmount,
        isActive: true
      }

      // First create the restaurant
      const restaurantResponse = await ApiService.createRestaurant(restaurantData)
      
      if (restaurantResponse.success) {
        // Then create the user account for the restaurant owner
        const userData = {
          email: formData.email,
          password: formData.password,
          name: formData.ownerName,
          phone: formData.phone,
          userType: 'restaurant_owner' as const,
          restaurantId: restaurantResponse.data._id,
          databaseName: restaurantResponse.data.databaseName,
          isActive: true
        }

        const userResponse = await ApiService.createUser(userData)
        
        if (userResponse.success) {
          onSuccess(restaurantResponse.data)
          setFormData(initialFormData)
          setGeneratedPassword('')
          setShowPassword(false)
          onClose()
        } else {
          // If user creation fails, we should ideally rollback restaurant creation
          setError(`Restaurant created but failed to create owner account: ${userResponse.message}`)
        }
      } else {
        setError(restaurantResponse.message || 'Failed to create restaurant')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create restaurant and owner account')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setError(null)
    setFormData(initialFormData)
    setGeneratedPassword('')
    setShowPassword(false)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Building2 className="h-5 w-5" />
            <span>Add New Restaurant</span>
          </DialogTitle>
          <DialogDescription>
            Create a new restaurant account with subscription
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-3">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-lg font-medium">Restaurant Details</h3>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Restaurant Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Mumbai Kitchen"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="ownerName">Owner Name *</Label>
                <Input
                  id="ownerName"
                  value={formData.ownerName}
                  onChange={(e) => handleInputChange('ownerName', e.target.value)}
                  placeholder="e.g., Raj Sharma"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="owner@restaurant.com"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+919876543210"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Select
                  value={formData.country}
                  onValueChange={(value) => handleInputChange('country', value)}
                >
                  <SelectTrigger id="country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Currency</Label>
                <div className="flex h-9 items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
                  {currencyCode} ({currencySymbol})
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="gstin">{isIndia ? 'GSTIN *' : 'Tax ID / VAT Number (optional)'}</Label>
              <Input
                id="gstin"
                value={formData.gstin}
                onChange={(e) => handleInputChange('gstin', e.target.value)}
                placeholder={isIndia ? '27ABCDE1234F1Z5' : 'Tax ID / VAT Number'}
                required={isIndia}
              />
            </div>
          </div>

          {/* Owner Account Password */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-3">
              <Key className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-lg font-medium">Owner Account Password</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGeneratePassword}
                  className="flex items-center space-x-2"
                >
                  <Key className="h-4 w-4" />
                  <span>Generate Secure Password</span>
                </Button>
                {generatedPassword && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(generatedPassword)}
                    className="flex items-center space-x-1"
                  >
                    <Copy className="h-3 w-3" />
                    <span>Copy</span>
                  </Button>
                )}
              </div>
              
              {showPassword && generatedPassword && (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Generated Password:</p>
                      <p className="text-lg font-mono bg-white px-2 py-1 rounded border text-gray-900 mt-1">
                        {generatedPassword}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    <strong>Important:</strong> Save this password securely. The restaurant owner will use this to login with their email.
                  </p>
                </div>
              )}
              
              {!generatedPassword && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700">
                    A secure password must be generated for the restaurant owner's login account.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-3">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-lg font-medium">Restaurant Address</h3>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="line1">Address Line 1 *</Label>
              <Input
                id="line1"
                value={formData.address.line1}
                onChange={(e) => handleInputChange('address.line1', e.target.value)}
                placeholder="123 Restaurant Street"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="line2">Address Line 2</Label>
              <Input
                id="line2"
                value={formData.address.line2}
                onChange={(e) => handleInputChange('address.line2', e.target.value)}
                placeholder="Near Market (Optional)"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  value={formData.address.city}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  placeholder="Mumbai"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="state">State *</Label>
                <Input
                  id="state"
                  value={formData.address.state}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  placeholder="Maharashtra"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="pincode">{isIndia ? 'Pincode *' : 'Postal Code *'}</Label>
                <Input
                  id="pincode"
                  value={formData.address.pincode}
                  onChange={(e) => handleInputChange('address.pincode', e.target.value)}
                  placeholder={isIndia ? '400001' : 'Postal code'}
                  required
                />
              </div>
            </div>
          </div>

          {/* Subscription Plan */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-3">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-lg font-medium">Subscription Details</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="planType">Plan Type</Label>
                <Select
                  value={formData.planType}
                  onValueChange={(value) => handleInputChange('planType', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yearly">Yearly Plan</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subscriptionAmount">Subscription Amount (₹) *</Label>
                <Input
                  id="subscriptionAmount"
                  type="number"
                  min="0"
                  value={formData.subscriptionAmount}
                  onChange={(e) => handleInputChange('subscriptionAmount', Number(e.target.value))}
                  placeholder="12000"
                  required
                />
              </div>
            </div>

            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">
                <strong>Subscription will be automatically activated</strong>
                <br />
                • Start Date: Today
                <br />
                • End Date: {new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                <br />
                • Amount: ₹{formData.subscriptionAmount.toLocaleString('en-IN')}/year
                <br />
                • Status: Active
                <br />
                • Payment: Paid
              </p>
            </div>
          </div>

          {/* Admin Notes */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-3">
              <User className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-lg font-medium">Admin Notes</h3>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="adminNotes">Internal Notes</Label>
              <Textarea
                id="adminNotes"
                value={formData.adminNotes}
                onChange={(e) => handleInputChange('adminNotes', e.target.value)}
                placeholder="Add any internal notes about this restaurant..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? 'Creating Restaurant...' : 'Create Restaurant'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}